import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, BOOKING_URL } from "@/data/content";
import { EASE } from "@/components/motion";

const BookLink = ({ testId, className, children, onClick }) =>
  BOOKING_URL ? (
    <a
      href={BOOKING_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={className}
      onClick={onClick}
    >
      {children}
    </a>
  ) : (
    <Link to="/kontakt" data-testid={testId} className={className} onClick={onClick}>
      {children}
    </Link>
  );

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="main-navigation-bar"
        className={`fixed inset-x-0 top-0 z-40 border-b transition-colors duration-500 ${
          scrolled ? "border-sand/80 bg-canvas/90 backdrop-blur-md" : "border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="nav-brand-logo-link" className="flex items-center leading-none">
            <img
              src="/images/logo.png"
              alt="Center for Hjerneregulering"
              data-testid="nav-brand-logo"
              className="h-11 w-auto sm:h-12"
            />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primær navigation">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-sm transition-colors duration-300 ${
                    isActive ? "text-charcoal" : "text-muted hover:text-charcoal"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <BookLink
              testId="nav-cta-book-button"
              className="border border-charcoal bg-charcoal px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-canvas transition-colors duration-300 hover:bg-transparent hover:text-charcoal"
            >
              Book samtale
            </BookLink>
          </nav>

          <button
            type="button"
            data-testid="nav-mobile-menu-toggle-btn"
            aria-label="Åbn menu"
            onClick={() => setOpen(true)}
            className="p-2 text-charcoal lg:hidden"
          >
            <Menu className="h-6 w-6" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex flex-col bg-canvas"
          >
            <div className="flex h-20 items-center justify-between px-4 sm:px-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-graphite">Menu</span>
              <button
                type="button"
                data-testid="mobile-menu-close-btn"
                aria-label="Luk menu"
                onClick={() => setOpen(false)}
                className="p-2 text-charcoal"
              >
                <X className="h-6 w-6" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center px-8" aria-label="Mobil navigation">
              {NAV_LINKS.map((l, i) => (
                <div key={l.to} className="overflow-hidden border-b border-hairline">
                  <motion.div
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "100%" }}
                    transition={{ duration: 0.55, ease: EASE, delay: 0.06 + i * 0.05 }}
                  >
                    <NavLink
                      to={l.to}
                      end={l.to === "/"}
                      data-testid={`mobile-${l.id}`}
                      className={({ isActive }) =>
                        `block py-3.5 font-serif text-4xl leading-tight tracking-tight transition-colors ${
                          isActive ? "italic text-charcoal" : "text-graphite"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                </div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.35, duration: 0.4 }}
              className="px-8 pb-10"
            >
              <BookLink
                testId="mobile-nav-cta-book-button"
                onClick={() => setOpen(false)}
                className="block border border-charcoal bg-charcoal px-6 py-4 text-center font-mono text-xs uppercase tracking-[0.2em] text-canvas"
              >
                Book en indledende samtale
              </BookLink>
              <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-muted">
                Neurofeedback · Selvregulering · Neuroplasticitet
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
